import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ChatCircle,
    CheckCircle,
    DotsThree,
    LockKey,
    MapPin,
    PencilSimple,
    ShieldSlash,
    Spinner,
    UsersThree,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PassportAppHeader from "@/components/PassportAppHeader";
import CommentsDrawer from "@/components/CommentsDrawer";
import FollowButton from "@/components/FollowButton";
import FriendButton from "@/components/FriendButton";
import FollowRosterModal from "@/components/FollowRosterModal";
import FriendsListModal from "@/components/FriendsListModal";
import FriendRequestsModal from "@/components/FriendRequestsModal";
import EditProfileModal from "@/components/EditProfileModal";
import ProfileReelsGrid from "@/components/ProfileReelsGrid";
import ReelModal from "@/components/ReelModal";
import PhotoLightbox from "@/components/PhotoLightbox";
import CreatorPlansPanel from "@/components/discover/CreatorPlansPanel";
import { ReportModal } from "@/components/PostCard";
import { ReviewCard, ReviewModal } from "@/components/ReviewsPanel";
import { usePassportShare } from "@/components/messages/PassportShareProvider";
import { friendsApi, messagesApi, postsApi, reviewsApi, toAbsolute, usersApi } from "@/lib/apiClient";

const TABS = [
    { key: "posts", label: "Posts" },
    { key: "reels", label: "Reels" },
    { key: "reviews", label: "Reviews" },
    { key: "trips", label: "Trips" },
    { key: "dates", label: "Dates" },
];

function ProfileShell({ children }) {
    return <div className="min-h-screen grain text-white">{children}</div>;
}

function StateCard({ title, description, ctaLabel, ctaHref, testId }) {
    return (
        <div className="min-h-[60vh] grid place-items-center px-6" data-testid={testId}>
            <div className="max-w-md text-center rounded-[32px] p-8" style={{ background: "rgba(7,12,20,0.92)", border: "1px solid rgba(212,175,55,0.16)" }}>
                <div className="w-16 h-16 mx-auto mb-5 rounded-full grid place-items-center" style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.28)" }}>
                    <LockKey size={26} color="#D4AF37" />
                </div>
                <h1 className="font-serif text-4xl text-white mb-3">{title}</h1>
                <p className="text-sm text-white/55 leading-relaxed">{description}</p>
                {ctaHref && ctaLabel && (
                    <Link to={ctaHref} className="btn-gold text-sm !py-2.5 !px-5 mt-6 inline-flex" data-testid={`${testId}-cta`}>
                        {ctaLabel}
                    </Link>
                )}
            </div>
        </div>
    );
}

function ProfileActionMenu({ open, onClose, controlState, busy, onToggleBlock, onToggleRestrict, onReport, handle }) {
    if (!open) return null;
    return (
        <div className="absolute right-0 top-12 z-30 min-w-[220px] rounded-3xl p-2" style={{ background: "rgba(7,12,20,0.98)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="profile-more-menu">
            <button type="button" onClick={onToggleRestrict} disabled={busy} className="w-full text-left px-4 py-3 rounded-2xl text-sm text-white/80 hover:bg-white/[0.04] transition-colors disabled:opacity-50" data-testid="profile-menu-restrict">
                {controlState === "restricted" ? `Remove restriction for @${handle}` : `Restrict @${handle}`}
            </button>
            <button type="button" onClick={onToggleBlock} disabled={busy} className="w-full text-left px-4 py-3 rounded-2xl text-sm text-[#F4A28F] hover:bg-white/[0.04] transition-colors disabled:opacity-50" data-testid="profile-menu-block">
                {controlState === "blocked" ? `Unblock @${handle}` : `Block @${handle}`}
            </button>
            <button type="button" onClick={onReport} className="w-full text-left px-4 py-3 rounded-2xl text-sm text-white/80 hover:bg-white/[0.04] transition-colors" data-testid="profile-menu-report">
                Report @${handle}
            </button>
            <button type="button" onClick={onClose} className="w-full text-left px-4 py-3 rounded-2xl text-sm text-white/45 hover:bg-white/[0.04] transition-colors" data-testid="profile-menu-close">
                Cancel
            </button>
        </div>
    );
}


function getPostMedia(post) {
    return post?.media?.length ? post.media : post?.image_url ? [{ url: post.image_url, kind: post?.is_reel ? "video" : "image" }] : [];
}

function hasVideoMedia(post) {
    return getPostMedia(post).some((item) => item?.kind === "video" || /\.(mp4|webm|mov)(\?|$)/i.test(item?.url || ""));
}

export default function Profile() {
    const { handle } = useParams();
    const nav = useNavigate();
    const { user } = useAuth() || {};
    const { openPassportShare } = usePassportShare();

    const [profile, setProfile] = useState(null);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [profileUnavailable, setProfileUnavailable] = useState(false);
    const [tab, setTab] = useState("posts");
    const [posts, setPosts] = useState([]);
    const [postsLoading, setPostsLoading] = useState(false);
    const [activePost, setActivePost] = useState(null);
    const [commentPost, setCommentPost] = useState(null);
    const [photoViewer, setPhotoViewer] = useState({ open: false, photos: [], initialIndex: 0 });
    const [reviews, setReviews] = useState([]);
    const [reviewsCursor, setReviewsCursor] = useState(null);
    const [reviewsLoading, setReviewsLoading] = useState(false);
    const [reviewsLoadingMore, setReviewsLoadingMore] = useState(false);
    const [controlState, setControlState] = useState("none");
    const [menuOpen, setMenuOpen] = useState(false);
    const [controlBusy, setControlBusy] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [editingReview, setEditingReview] = useState(null);
    const [editProfileOpen, setEditProfileOpen] = useState(false);
    const [friendRequestsOpen, setFriendRequestsOpen] = useState(false);
    const [friendsListOpen, setFriendsListOpen] = useState(false);
    const [rosterOpen, setRosterOpen] = useState(null);
    const [pendingRequestCount, setPendingRequestCount] = useState(0);

    const isOwner = Boolean(user && profile?.viewer_is_owner);
    const canViewContent = Boolean(profile && !profile.is_locked);
    const profileHandle = profile?.handle;
    const viewerIsOwner = profile?.viewer_is_owner;

    const loadProfile = useCallback(async () => {
        setLoadingProfile(true);
        setProfileUnavailable(false);
        try {
            const data = await usersApi.profile(handle);
            setProfile(data);
            if (data?.handle && data.handle !== handle) {
                nav(`/profile/${data.handle}`, { replace: true });
            }
        } catch {
            setProfile(null);
            setProfileUnavailable(true);
        } finally {
            setLoadingProfile(false);
        }
    }, [handle, nav]);

    const loadPosts = useCallback(async () => {
        if (!canViewContent) return;
        setPostsLoading(true);
        try {
            const response = await postsApi.list({ handle, limit: 60 });
            setPosts((response.items || []).filter((item) => !item.is_reel));
        } catch {
            setPosts([]);
        } finally {
            setPostsLoading(false);
        }
    }, [canViewContent, handle]);

    const loadReviews = useCallback(async (cursor = null) => {
        if (!canViewContent) return;
        cursor ? setReviewsLoadingMore(true) : setReviewsLoading(true);
        try {
            const response = await usersApi.reviews(handle, { limit: 8, ...(cursor ? { cursor } : {}) });
            const items = response.items || [];
            setReviews((prev) => (cursor ? [...prev, ...items] : items));
            setReviewsCursor(response.next_cursor || null);
        } catch {
            if (!cursor) setReviews([]);
            setReviewsCursor(null);
        } finally {
            setReviewsLoading(false);
            setReviewsLoadingMore(false);
        }
    }, [canViewContent, handle]);


    useEffect(() => {
        loadProfile();
    }, [loadProfile]);

    useEffect(() => {
        if (!profile || profile.is_locked) {
            setPosts([]);
            setReviews([]);
            return;
        }
        loadPosts();
        loadReviews();
    }, [profile?.id, profile?.is_locked, loadPosts, loadReviews]);

    useEffect(() => {
        if (!user || !profileHandle || viewerIsOwner) return;
        messagesApi.listControls()
            .then((items) => {
                const match = (items || []).find((item) => item?.target?.handle === profileHandle);
                setControlState(match?.relation || "none");
            })
            .catch(() => setControlState("none"));
    }, [user, profileHandle, viewerIsOwner]);

    useEffect(() => {
        if (!user || !viewerIsOwner) return;
        friendsApi.myRequests().then((items) => setPendingRequestCount(Array.isArray(items) ? items.length : 0)).catch(() => setPendingRequestCount(0));
    }, [user, viewerIsOwner]);

    const handleDeleteReview = async (reviewId) => {
        if (!window.confirm("Delete this review?")) return;
        try {
            await reviewsApi.delete(reviewId);
            toast.success("Review deleted.");
            setReviews((prev) => prev.filter((item) => item.id !== reviewId));
        } catch {
            toast.error("Could not delete this review.");
        }
    };

    const handleReviewUpdate = (reviewId, patch) => {
        setReviews((prev) => prev.map((item) => (item.id === reviewId ? { ...item, ...patch } : item)));
    };

    const handleToggleBlock = async () => {
        if (!profile?.handle) return;
        setControlBusy(true);
        try {
            if (controlState === "blocked") {
                await messagesApi.unblockUser(profile.handle);
                setControlState("none");
                toast.success(`@${profile.handle} unblocked.`);
            } else {
                await messagesApi.blockUser(profile.handle);
                toast.success(`@${profile.handle} blocked.`);
                nav("/app");
            }
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not update this control.");
        } finally {
            setControlBusy(false);
            setMenuOpen(false);
        }
    };

    const handleToggleRestrict = async () => {
        if (!profile?.handle) return;
        setControlBusy(true);
        try {
            if (controlState === "restricted") {
                await messagesApi.unrestrictUser(profile.handle);
                setControlState("none");
                toast.success(`Restriction removed for @${profile.handle}.`);
            } else {
                await messagesApi.restrictUser(profile.handle);
                setControlState("restricted");
                toast.success(`@${profile.handle} restricted.`);
            }
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not update this control.");
        } finally {
            setControlBusy(false);
            setMenuOpen(false);
        }
    };

    const messageHref = profile?.messaging?.conversation_id
        ? `/messages?conv=${profile.messaging.conversation_id}`
        : profile?.handle
            ? `/messages?compose=${profile.handle}`
            : "/messages";
    const profileStats = useMemo(() => ([
        { key: "followers", label: "Followers", value: profile?.followers ?? 0 },
        { key: "following", label: "Following", value: profile?.following ?? 0 },
        { key: "friends", label: "Friends", value: profile?.friends_count ?? 0 },
        { key: "countries", label: "Countries", value: profile?.countries_visited ?? 0 },
    ]), [profile]);

    return (
        <ProfileShell>
            <PassportAppHeader homeHref="/app" subtitle="Profile" maxWidthClass="max-w-[1240px]" />

            {loadingProfile ? (
                <div className="min-h-[70vh] grid place-items-center text-white/60" data-testid="profile-loading">
                    <Spinner size={28} className="animate-spin" />
                </div>
            ) : profileUnavailable || !profile ? (
                <StateCard
                    title="Passport profile unavailable"
                    description="This traveler is unavailable right now. They may not exist anymore, or Passport privacy settings prevent access from your account."
                    ctaLabel="Back to Passport"
                    ctaHref="/app"
                    testId="profile-unavailable-state"
                />
            ) : (
            <div className="max-w-[1240px] mx-auto px-6 md:px-10 pt-4 pb-8 md:pt-6 md:pb-10">
                <section className="rounded-[36px] overflow-hidden mb-8 border" style={{ background: "linear-gradient(140deg, rgba(212,175,55,0.12), rgba(5,10,17,0.88) 48%, rgba(15,28,46,0.94))", borderColor: "rgba(212,175,55,0.18)" }} data-testid="profile-page">
                    {profile.cover_url && (
                        <div className="relative aspect-[16/3.2] min-h-[150px] bg-black/30">
                            <img src={toAbsolute(profile.cover_url)} alt="" className="w-full h-full object-cover" />
                            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.15), rgba(5,10,17,0.92))" }} />
                        </div>
                    )}

                    <div className={`px-5 md:px-8 pb-8 md:pb-10 relative z-10 ${profile.cover_url ? "-mt-12" : "pt-6 md:pt-8"}`}>
                        <div className="grid gap-5 lg:grid-cols-[auto_minmax(0,1fr)_260px] lg:items-start">
                            <img
                                src={profile.avatar_url || `https://api.dicebear.com/7.x/notionists/svg?seed=${profile.handle}`}
                                alt={profile.name || profile.handle}
                                className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover"
                                style={{ border: "3px solid rgba(212,175,55,0.65)", boxShadow: "0 14px 34px rgba(0,0,0,0.26)" }}
                                data-testid="profile-avatar"
                            />

                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-3">
                                    <h1 className="font-serif text-2xl md:text-3xl text-white tracking-tight truncate" data-testid="profile-name">{profile.name || profile.handle}</h1>
                                    {profile.is_verified && <CheckCircle size={18} weight="fill" color="#D4AF37" data-testid="profile-verified-badge" />}
                                    {profile.is_private && (
                                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.2em]" style={{ background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.72)" }} data-testid="profile-private-badge">
                                            <LockKey size={11} /> Private account
                                        </span>
                                    )}
                                </div>

                                <div className="text-sm mt-2" style={{ color: "var(--gold)" }} data-testid="profile-handle">@{profile.handle}</div>

                                {Boolean(profile.location || profile.travel_preview?.length) && (
                                    <div className="flex flex-wrap items-center gap-2 mt-4">
                                        {profile.location && (
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs" style={{ background: "rgba(255,255,255,0.06)", color: "white" }} data-testid="profile-location-chip">
                                                <MapPin size={12} color="#D4AF37" /> {profile.location}
                                            </span>
                                        )}
                                        {(profile.travel_preview || []).map((country, idx) => (
                                            <span key={`${country}-${idx}`} className="px-3 py-1.5 rounded-full text-xs" style={{ background: "rgba(159,213,208,0.14)", color: "#9FD5D0" }} data-testid={`profile-travel-preview-${country.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                                {country}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {profile.bio && <p className="text-sm md:text-base text-white/70 leading-relaxed mt-4 max-w-3xl" data-testid="profile-bio">{profile.bio}</p>}

                                {profile.follow_state === "incoming_request" && (
                                    <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs" style={{ background: "rgba(212,175,55,0.12)", color: "var(--gold)" }} data-testid="profile-follow-requested-you-state">
                                        <UsersThree size={13} /> This traveler has requested to follow you.
                                    </div>
                                )}

                                <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mt-5">
                                    {profileStats.map((item) => {
                                        const buttonProps = item.key === "followers"
                                            ? { onClick: () => setRosterOpen("followers"), testId: "profile-followers-button" }
                                            : item.key === "following"
                                                ? { onClick: () => setRosterOpen("following"), testId: "profile-following-button" }
                                                : item.key === "friends"
                                                    ? { onClick: () => setFriendsListOpen(true), testId: "profile-friends-button" }
                                                    : null;
                                        return buttonProps ? (
                                            <button key={item.key} type="button" onClick={buttonProps.onClick} disabled={profile.is_locked && !isOwner} className="text-left rounded-2xl px-4 py-3 min-w-0 disabled:opacity-60" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={buttonProps.testId}>
                                                <div className="text-2xl font-serif text-white">{item.value}</div>
                                                <div className="text-[11px] uppercase tracking-[0.2em] text-white/45 mt-1">{item.label}</div>
                                            </button>
                                        ) : (
                                            <div key={item.key} className="text-left rounded-2xl px-4 py-3 min-w-0" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={`profile-stat-${item.key}`}>
                                                <div className="text-2xl font-serif text-white">{item.value}</div>
                                                <div className="text-[11px] uppercase tracking-[0.2em] text-white/45 mt-1">{item.label}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="w-full lg:max-w-[260px] lg:justify-self-end">
                                <div className="rounded-[28px] p-4 md:p-5" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/38 mb-4">Actions</div>
                                    <div className="flex flex-wrap gap-2" data-testid="profile-actions">
                                        {!isOwner && <FollowButton handle={profile.handle} initial={profile.follow_state} onStateChange={() => loadProfile()} />}
                                        {!isOwner && <FriendButton handle={profile.handle} />}
                                        {!isOwner && profile.messaging?.can_message && (
                                            <Link to={messageHref} className="inline-flex items-center gap-1.5 rounded-full text-xs font-medium transition-colors" style={{ padding: "5px 12px", border: "1px solid rgba(212,175,55,0.35)", color: "var(--gold)", background: "rgba(212,175,55,0.08)" }} data-testid="profile-message-button">
                                                <ChatCircle size={12} /> {profile.messaging?.mode === "request" ? "Message request" : "Message"}
                                            </Link>
                                        )}
                                        {isOwner && (
                                            <button type="button" onClick={() => setEditProfileOpen(true)} className="inline-flex items-center gap-1.5 rounded-full text-xs font-medium transition-colors" style={{ padding: "5px 12px", border: "1px solid rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.72)", background: "rgba(255,255,255,0.05)" }} data-testid="profile-edit-button">
                                                <PencilSimple size={12} /> Edit profile
                                            </button>
                                        )}
                                        {isOwner && (
                                            <button type="button" onClick={() => setFriendRequestsOpen(true)} className="inline-flex items-center gap-1.5 rounded-full text-xs font-medium transition-colors" style={{ padding: "5px 12px", border: "1px solid rgba(212,175,55,0.35)", color: "var(--gold)", background: "rgba(212,175,55,0.08)" }} data-testid="profile-friend-requests-button">
                                                <UsersThree size={12} weight="fill" /> Friend requests
                                                {pendingRequestCount > 0 && <span className="min-w-[18px] h-[18px] rounded-full grid place-items-center px-1 text-[10px] font-bold" style={{ background: "var(--gold)", color: "var(--navy)" }}>{pendingRequestCount}</span>}
                                            </button>
                                        )}
                                        {!isOwner && (
                                            <div className="relative ml-auto">
                                                <button type="button" onClick={() => setMenuOpen((value) => !value)} className="w-9 h-9 rounded-full grid place-items-center" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="profile-more-button">
                                                    <DotsThree size={16} />
                                                </button>
                                                <ProfileActionMenu
                                                    open={menuOpen}
                                                    onClose={() => setMenuOpen(false)}
                                                    controlState={controlState}
                                                    busy={controlBusy}
                                                    onToggleBlock={handleToggleBlock}
                                                    onToggleRestrict={handleToggleRestrict}
                                                    onReport={() => {
                                                        setReportOpen(true);
                                                        setMenuOpen(false);
                                                    }}
                                                    handle={profile.handle}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {!isOwner && !profile.messaging?.can_message && (
                                        <div className="mt-4 rounded-2xl px-4 py-3 text-sm text-white/50" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="profile-message-unavailable-state">
                                            Passport messaging is unavailable for this account because of current privacy or relationship settings.
                                        </div>
                                    )}

                                    {controlState === "restricted" && (
                                        <div className="mt-4 rounded-2xl px-4 py-3 text-sm" style={{ background: "rgba(159,213,208,0.08)", border: "1px solid rgba(159,213,208,0.18)", color: "#9FD5D0" }} data-testid="profile-restricted-state">
                                            <div className="inline-flex items-center gap-2"><ShieldSlash size={14} /> You’ve restricted this traveler.</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {profile.is_locked ? (
                    <StateCard
                        title="This account is private"
                        description={profile.follow_state === "requested" ? `Your follow request to @${profile.handle} is still pending.` : `Follow @${profile.handle} or connect as friends to see their Posts, Reels, Reviews, and Travel.`}
                        testId="profile-private-state"
                    />
                ) : (
                    <>
                        <div className="flex gap-2 flex-wrap mb-6 p-1 rounded-full w-max" style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="profile-tabs">
                            {TABS.map((item) => (
                                <button key={item.key} type="button" onClick={() => setTab(item.key)} className="px-4 py-2 rounded-full text-xs transition-colors" style={{ background: tab === item.key ? "var(--gold)" : "transparent", color: tab === item.key ? "var(--navy)" : "white" }} data-testid={`profile-tab-${item.key}`}>
                                    {item.label}
                                </button>
                            ))}
                        </div>

                        {tab === "posts" && (
                            <div data-testid="profile-posts-panel">
                                {postsLoading ? (
                                    <div className="py-16 text-center text-white/45">Loading posts…</div>
                                ) : posts.length === 0 ? (
                                    <div className="py-16 text-center rounded-[28px]" style={{ background: "var(--navy-deep)", border: "1px dashed rgba(255,255,255,0.1)" }} data-testid="profile-posts-empty">
                                        <div className="text-white/50 text-sm">No public posts here yet.</div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
                                        {posts.map((item) => {
                                            const media = getPostMedia(item)[0] || null;
                                            if (!media) return null;
                                            return (
                                                <button key={item.id} type="button" onClick={() => {
                                                    if (hasVideoMedia(item)) {
                                                        setActivePost(item);
                                                        return;
                                                    }
                                                    setPhotoViewer({ open: true, photos: getPostMedia(item).map((entry) => entry.url), initialIndex: 0 });
                                                }} className="relative aspect-square rounded-[28px] overflow-hidden group text-left" data-testid={`profile-post-card-${item.id}`}>
                                                    {media.kind === "video" || media.url?.includes(".mp4") ? (
                                                        <video src={toAbsolute(media.url)} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" muted playsInline />
                                                    ) : (
                                                        <img src={toAbsolute(media.url)} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                                                    )}
                                                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.08), rgba(0,0,0,0.75))" }} />
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {tab === "reels" && <ProfileReelsGrid handle={handle} />}

                        {tab === "reviews" && (
                            <div data-testid="profile-reviews-panel">
                                {reviewsLoading ? (
                                    <div className="py-16 text-center text-white/45">Loading reviews…</div>
                                ) : reviews.length === 0 ? (
                                    <div className="py-16 text-center rounded-[28px]" style={{ background: "var(--navy-deep)", border: "1px dashed rgba(255,255,255,0.1)" }} data-testid="profile-reviews-empty">
                                        <div className="text-white/50 text-sm">No public reviews here yet.</div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            {reviews.map((item) => (
                                                <ReviewCard key={item.id} r={item} user={user} onEdit={setEditingReview} onDelete={handleDeleteReview} onUpdate={handleReviewUpdate} />
                                            ))}
                                        </div>
                                        {reviewsCursor && (
                                            <div className="pt-5 text-center">
                                                <button type="button" onClick={() => loadReviews(reviewsCursor)} disabled={reviewsLoadingMore} className="btn-ghost text-sm !py-2.5 !px-5" data-testid="profile-reviews-load-more">
                                                    {reviewsLoadingMore ? "Loading more reviews…" : "Load more reviews"}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {tab === "trips" && <CreatorPlansPanel handle={profile?.handle || handle} type="trip" isOwner={isOwner} />}

                        {tab === "dates" && <CreatorPlansPanel handle={profile?.handle || handle} type="traveldate" isOwner={isOwner} />}
                    </>
                )}
            </div>
            )}

            <CommentsDrawer post={commentPost} open={!!commentPost} onClose={() => setCommentPost(null)} />
            <PhotoLightbox open={photoViewer.open} photos={photoViewer.photos} initialIndex={photoViewer.initialIndex} onClose={() => setPhotoViewer({ open: false, photos: [], initialIndex: 0 })} />

            <ReviewModal
                open={!!editingReview}
                initialData={editingReview}
                onClose={() => setEditingReview(null)}
                onSaved={() => {
                    setEditingReview(null);
                    loadReviews();
                }}
            />

            <AnimatePresence>
                {activePost && hasVideoMedia(activePost) && (
                    <ReelModal
                        reel={{
                            ...activePost,
                            is_post: true,
                            id: activePost.reel_id || activePost.id,
                            media: getPostMedia(activePost),
                            likes: activePost.reactions_total || activePost.likes || 0,
                            comments_count: activePost.comments || 0,
                            liked: !!activePost.my_reaction,
                            saved: activePost.saved,
                            views: activePost.views ?? 0,
                        }}
                        user={user}
                        onClose={() => setActivePost(null)}
                        onLike={async () => {
                            try {
                                await postsApi.react(activePost.id, "like");
                                loadPosts();
                            } catch {
                                toast.error("Could not update this post.");
                            }
                        }}
                        onSave={async () => {
                            try {
                                if (activePost.saved) await postsApi.unsave(activePost.id);
                                else await postsApi.save(activePost.id);
                                loadPosts();
                            } catch {
                                toast.error("Could not update this post.");
                            }
                        }}
                        onShare={() => openPassportShare({ shareRef: { entity_type: activePost.reel_id ? "reel" : "post", entity_id: activePost.reel_id || activePost.id }, sourceLabel: activePost.reel_id ? "this Reel" : "this Post" })}
                        onOpenComments={() => setCommentPost(activePost)}
                        onReport={() => setReportOpen(true)}
                        onPrev={() => {
                            const index = posts.findIndex((item) => item.id === activePost.id);
                            if (index > 0) setActivePost(posts[index - 1]);
                        }}
                        onNext={() => {
                            const index = posts.findIndex((item) => item.id === activePost.id);
                            if (index < posts.length - 1) setActivePost(posts[index + 1]);
                        }}
                    />
                )}
            </AnimatePresence>

            <ReportModal open={reportOpen} onClose={() => setReportOpen(false)} targetType="user" targetId={profile?.id} />

            <FriendRequestsModal
                open={friendRequestsOpen}
                onClose={() => setFriendRequestsOpen(false)}
                onFriendAccepted={() => setPendingRequestCount((value) => Math.max(0, value - 1))}
            />

            {profile && friendsListOpen && (
                <FriendsListModal
                    handle={profile.handle}
                    isOwnProfile={isOwner}
                    onClose={() => setFriendsListOpen(false)}
                    onFriendRemoved={() => loadProfile()}
                />
            )}

            {profile && rosterOpen && <FollowRosterModal handle={profile.handle} initialTab={rosterOpen} onClose={() => setRosterOpen(null)} />}

            <EditProfileModal
                open={editProfileOpen}
                profile={profile}
                onClose={() => setEditProfileOpen(false)}
                onSaved={(updated) => {
                    setProfile((prev) => (prev ? { ...prev, ...updated } : prev));
                    if (updated.handle && updated.handle !== handle) nav(`/profile/${updated.handle}`, { replace: true });
                }}
            />
        </ProfileShell>
    );
}